import { useTranslation } from 'react-i18next';
import { Center, Spinner, VStack } from '@chakra-ui/react';
import { SectionCard } from '@themes/aegix';
import { DetailRow } from '../DetailRow';
import { useAlertHistoryDetail } from '../../../hooks/queries/useAlerts';
import type { AlertHistory } from '../../../services/alerts.service';

function formatName(first: string | null, last: string | null): string {
  if (first && last) return `${first} ${last}`;
  return first ?? last ?? '---';
}

interface AlertDetailOverviewProps {
  alertId: number;
}

export function AlertDetailOverview({ alertId }: AlertDetailOverviewProps) {
  const { t } = useTranslation();
  const { data: alert, isLoading } = useAlertHistoryDetail(alertId);

  if (isLoading || !alert) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  const getStatusLabel = (item: AlertHistory): string => {
    if (item.pending_approval) return t('events.pendingApproval', 'Pending Approval');
    if (item.cancelled) return t('events.ended', 'Ended');
    return t('common.active', 'Active');
  };

  const getFlags = (item: AlertHistory): string => {
    const flags: string[] = [];
    if (item.silent) flags.push(t('events.silent', 'Silent'));
    if (item.scoped) flags.push(t('events.scoped', 'Scoped'));
    return flags.join(', ');
  };

  return (
    <SectionCard title={t('events.alertDetails', 'Alert Details')}>
      <VStack align="stretch" gap={0}>
        <DetailRow label={t('common.school', 'School')} value={alert.site_name} dataE2e="alert-detail-school" />
        <DetailRow
          label={t('common.type', 'Type')}
          value={alert.internal ? t('events.drill', 'Drill') : t('events.alert', 'Alert')}
          dataE2e="alert-detail-type"
        />
        <DetailRow label={t('events.alertType', 'Alert Type')} value={alert.alert_type_name ?? '---'} dataE2e="alert-detail-alert-type" />
        <DetailRow label={t('common.status', 'Status')} value={getStatusLabel(alert)} dataE2e="alert-detail-status" />
        <DetailRow label={t('events.policeStatus', 'Police Status')} value={alert.last_police_status || '---'} dataE2e="alert-detail-police-status" />
        <DetailRow
          label={t('events.initiatedBy', 'Initiated By')}
          value={formatName(alert.initiator_first_name, alert.initiator_last_name)}
          dataE2e="alert-detail-initiated-by"
        />
        <DetailRow
          label={t('events.started', 'Started')}
          value={new Date(alert.created).toLocaleString()}
          dataE2e="alert-detail-started"
        />
        {alert.cancelled && (
          <>
            <DetailRow
              label={t('events.ended', 'Ended')}
              value={new Date(alert.cancelled).toLocaleString()}
              dataE2e="alert-detail-ended"
            />
            <DetailRow
              label={t('events.cancelledBy', 'Cancelled By')}
              value={formatName(alert.canceller_first_name, alert.canceller_last_name)}
              dataE2e="alert-detail-cancelled-by"
            />
          </>
        )}
        {(alert.silent || alert.scoped) && (
          <DetailRow
            label={t('events.flags', 'Flags')}
            value={getFlags(alert)}
            dataE2e="alert-detail-flags"
          />
        )}
      </VStack>
    </SectionCard>
  );
}
